import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { Copy } from "@/assets/icons/ds";
import { useAlert } from "@/hooks/useAlert";
import { AlertTypes } from "@/utils/atoms";

interface Props {
  dataTestid?: string;
  className?: string;
  content: string;
  successMessage: string;
  errorMessage: string;
  disabled?: boolean;
}

/** A read-only field showing a value with a copy-to-clipboard action. */
export const ClipboardField: FC<Props> = ({
  dataTestid,
  className,
  content,
  successMessage,
  errorMessage,
  disabled,
}) => {
  const showAlert = useAlert();
  const { t } = useTranslation("translation");

  return (
    <button
      data-testid={dataTestid}
      className={clsx(
        "flex min-h-14 w-full items-center gap-3 rounded-md bg-surface-tertiary px-5 py-2 text-left text-body-base font-semibold leading-md",
        disabled
          ? "cursor-not-allowed text-foreground-tertiary"
          : "cursor-pointer text-foreground-primary",
        className,
      )}
      type="button"
      aria-label={t("clipboardField.copy")}
      aria-disabled={disabled}
      disabled={disabled}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(content);
          showAlert({
            type: AlertTypes.info,
            text: successMessage,
          });
        } catch {
          showAlert({
            type: AlertTypes.error,
            text: errorMessage,
          });
        }
      }}
    >
      {/* break-all, not truncate: the design wraps the invitation link over
          several lines once the card gets narrow. */}
      <span className="min-w-0 flex-1 break-all">{content}</span>
      <Copy className="size-6 shrink-0" />
    </button>
  );
};
